import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  Archive,
  FileText,
  LoaderCircle,
  Megaphone,
  MessagesSquare,
  X,
} from 'lucide-react';
import { getMessageDisplayParts } from '../utils/messageDisplay';
import '../styles/AdminConversationReviewDrawer.css';

function formatTimestamp(value) {
  if (!value) return 'Time unavailable';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Time unavailable';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function campaignLabel(campaign) {
  const archiveLabel = campaign.archived ? ' · Archived' : '';
  return `${campaign.name || campaign.id} · ${campaign.status}${archiveLabel}`;
}

function threadLabel(thread) {
  return thread.propertyName || thread.propertyAddress || thread.subject || thread.id;
}

function addressLabel(value) {
  if (!value) return 'Unknown sender';
  return value.name ? `${value.name} <${value.address}>` : value.address;
}

function recipientLabel(values) {
  if (!values?.length) return 'Recipients unavailable';
  return values.map(addressLabel).join(', ');
}

function removeAdminFetchingMarkup(body) {
  const rawContent = typeof body === 'object' ? body?.content : body;
  if (!rawContent || !/<[a-z][\s\S]*>/i.test(String(rawContent))) return body;

  const template = document.createElement('template');
  template.innerHTML = String(rawContent);
  template.content
    .querySelectorAll('img, picture, source, video, audio, track, link, iframe, object, embed, svg, image, use, meta, base, input[type="image"]')
    .forEach(element => element.remove());
  template.content.querySelectorAll('*').forEach(element => {
    ['src', 'srcset', 'poster', 'background', 'data', 'style', 'ping'].forEach(attribute => {
      element.removeAttribute(attribute);
    });
  });

  const safeContent = template.innerHTML;
  return typeof body === 'object' ? { ...body, content: safeContent } : safeContent;
}

function SanitizedBody({ body, bodyTruncated = false, testId }) {
  const parts = getMessageDisplayParts(removeAdminFetchingMarkup(body));
  return (
    <>
      <div
        className="admin-review-message__body"
        data-testid={testId}
        dangerouslySetInnerHTML={{ __html: parts.html }}
      />
      {parts.wasTrimmed && (
        <span className="admin-review-message__trimmed">Quoted history hidden</span>
      )}
      {bodyTruncated && (
        <span className="admin-review-message__shortened">Content shortened</span>
      )}
    </>
  );
}

function DrawerState({ icon: Icon, children, tone = 'neutral' }) {
  return (
    <div
      className={`admin-review-state admin-review-state--${tone}`}
      role={tone === 'error' ? 'alert' : 'status'}
    >
      <Icon size={21} aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

function PreservedDraft({ draft, referenceNote = '' }) {
  return (
    <article className="admin-review-draft" aria-label="Preserved SiteSift draft">
      <header>
        <FileText size={17} aria-hidden="true" />
        <div>
          <strong>Preserved SiteSift draft</strong>
          <span>{draft.status} · {formatTimestamp(draft.createdAt)}</span>
        </div>
      </header>
      {referenceNote && <p className="admin-review-draft__reference">{referenceNote}</p>}
      {draft.recipient && <p>Intended for {draft.recipient}</p>}
      {draft.subject && <div className="admin-review-message__subject">{draft.subject}</div>}
      <SanitizedBody
        body={draft.body}
        bodyTruncated={draft.bodyTruncated}
        testId="review-draft-body"
      />
    </article>
  );
}

function CampaignDrafts({ drafts, threadsTruncated }) {
  return (
    <section className="admin-review-campaign-drafts" aria-label="Campaign preserved drafts">
      <header className="admin-review-campaign-drafts__header">
        <div>
          <strong>Preserved drafts</strong>
          <span>Drafts without a returned campaign thread</span>
        </div>
        <strong>{drafts.length}</strong>
      </header>
      {drafts.map(draft => {
        let referenceNote = 'No thread reference';
        if (draft.threadId) {
          referenceNote = threadsTruncated
            ? `Thread ${draft.threadId} may be outside the 100-thread review limit.`
            : `Referenced thread ${draft.threadId} is not in this review.`;
        }
        return <PreservedDraft draft={draft} referenceNote={referenceNote} key={draft.id} />;
      })}
    </section>
  );
}

export default function AdminConversationReviewDrawer({
  user,
  review = {},
  selectedClientId = null,
  selectedThreadId = null,
  loading = false,
  threadLoading = false,
  error = '',
  threadError = '',
  onSelectCampaign,
  onSelectThread,
  onClose,
}) {
  const campaigns = useMemo(
    () => (Array.isArray(review.campaigns) ? review.campaigns : []),
    [review.campaigns]
  );
  const threads = useMemo(
    () => (Array.isArray(review.threads) ? review.threads : []),
    [review.threads]
  );
  const drafts = useMemo(
    () => (Array.isArray(review.drafts) ? review.drafts : []),
    [review.drafts]
  );
  const returnedThreadIds = useMemo(
    () => new Set(threads.map(thread => thread.id)),
    [threads]
  );
  const campaignDrafts = useMemo(
    () => drafts.filter(draft => !draft.threadId || !returnedThreadIds.has(draft.threadId)),
    [drafts, returnedThreadIds]
  );
  const selectedThread = threads.find(thread => thread.id === selectedThreadId) || threads[0] || null;
  const messages = useMemo(() => (
    [...(selectedThread?.messages || [])].sort((left, right) => {
      const timeComparison = String(left.sentAt || '').localeCompare(String(right.sentAt || ''));
      return timeComparison || left.id.localeCompare(right.id);
    })
  ), [selectedThread]);
  const selectedDrafts = drafts.filter(draft => draft.threadId === selectedThread?.id);
  const resultLimitPartial = review.truncation?.campaigns === true
    || review.truncation?.threads === true
    || review.truncation?.drafts === true
    || (review.truncation?.messageThreads || []).length > 0
    || threads.some(thread => thread.responseBudgetTruncated);
  const showPartial = selectedClientId ? resultLimitPartial : review.truncation?.campaigns === true;
  const showMetadataShortening = (review.truncation?.metadata?.records || 0) > 0
    || (review.truncation?.metadata?.recipientLists || 0) > 0;
  const shortenedMessages = review.truncation?.contentBodies?.messages || 0;
  const shortenedDrafts = review.truncation?.contentBodies?.drafts || 0;
  const shortenedCopy = shortenedMessages && shortenedDrafts
    ? 'Some message and draft content was shortened.'
    : shortenedMessages
      ? 'Some message content was shortened.'
      : 'Some draft content was shortened.';

  let content;
  if (loading) {
    content = (
      <DrawerState icon={LoaderCircle}>
        Loading conversation review
      </DrawerState>
    );
  } else if (error) {
    content = <DrawerState icon={AlertTriangle} tone="error">{error}</DrawerState>;
  } else if (!campaigns.length) {
    content = <DrawerState icon={Archive}>No campaigns available for this user.</DrawerState>;
  } else if (!selectedClientId) {
    content = <DrawerState icon={Megaphone}>Choose a campaign to review its SiteSift conversations.</DrawerState>;
  } else if (!threads.length && campaignDrafts.length) {
    content = (
      <div className="admin-review-draft-only">
        <CampaignDrafts drafts={campaignDrafts} threadsTruncated={review.truncation?.threads === true} />
      </div>
    );
  } else if (!threads.length) {
    content = <DrawerState icon={MessagesSquare}>No campaign conversations found.</DrawerState>;
  } else {
    content = (
      <div className="admin-review-workspace">
        <nav className="admin-review-threads" aria-label="Campaign threads">
          <div className="admin-review-section-heading">
            <span>Threads</span>
            <strong>{threads.length}</strong>
          </div>
          <div className="admin-review-thread-list">
            {threads.map(thread => (
              <button
                type="button"
                className={thread.id === selectedThread?.id ? 'is-selected' : ''}
                aria-pressed={thread.id === selectedThread?.id}
                onClick={() => onSelectThread(thread.id)}
                key={thread.id}
              >
                <MessagesSquare size={16} aria-hidden="true" />
                <span>
                  <strong>{threadLabel(thread)}</strong>
                  {thread.propertyName && thread.propertyAddress && <small>{thread.propertyAddress}</small>}
                  <small>
                    {thread.status} · {thread.messagesLoaded ? `${thread.messageCount} messages` : 'Messages not loaded'}
                  </small>
                </span>
              </button>
            ))}
          </div>
        </nav>

        <section className="admin-review-conversation" aria-label="Selected conversation">
          <header className="admin-review-conversation__header">
            <div>
              <span>{selectedThread.status}</span>
              <h3>{threadLabel(selectedThread)}</h3>
              {selectedThread.subject && <p>{selectedThread.subject}</p>}
            </div>
            <time dateTime={selectedThread.updatedAt || undefined}>
              {formatTimestamp(selectedThread.updatedAt)}
            </time>
          </header>

          {selectedThread.messagesTruncated && (
            <div className="admin-review-thread-limit" role="status">
              <AlertTriangle size={15} aria-hidden="true" />
              This thread reached the 100-message review limit.
            </div>
          )}

          {selectedThread.responseBudgetTruncated && (
            <div className="admin-review-thread-limit" role="status">
              <AlertTriangle size={15} aria-hidden="true" />
              Earlier messages were omitted to keep this review loadable.
            </div>
          )}

          {threadLoading ? (
            <DrawerState icon={LoaderCircle}>Loading selected conversation</DrawerState>
          ) : threadError ? (
            <DrawerState icon={AlertTriangle} tone="error">{threadError}</DrawerState>
          ) : selectedThread.messagesLoaded ? (
            <div className="admin-review-message-list">
              {messages.map(message => (
                <article
                  className={`admin-review-message admin-review-message--${message.direction}`}
                  key={message.id}
                >
                  <header>
                    <div>
                      <strong>{message.direction === 'outbound' ? 'SiteSift' : 'Broker'}</strong>
                      <span>From {addressLabel(message.sender)}</span>
                      <span>To {recipientLabel(message.recipients)}</span>
                      {message.recipientsTruncated && <span>Additional recipients omitted</span>}
                    </div>
                    <time dateTime={message.sentAt || undefined}>{formatTimestamp(message.sentAt)}</time>
                  </header>
                  {message.subject && <div className="admin-review-message__subject">{message.subject}</div>}
                  <SanitizedBody
                    body={message.body}
                    bodyTruncated={message.bodyTruncated}
                    testId="review-message-body"
                  />
                </article>
              ))}
            </div>
          ) : (
            <DrawerState icon={MessagesSquare}>Conversation not loaded.</DrawerState>
          )}

          {selectedDrafts.map(draft => <PreservedDraft draft={draft} key={draft.id} />)}

          {campaignDrafts.length > 0 && (
            <CampaignDrafts
              drafts={campaignDrafts}
              threadsTruncated={review.truncation?.threads === true}
            />
          )}
        </section>
      </div>
    );
  }

  const identity = user?.displayName || user?.email || user?.uid || 'Unknown user';
  return createPortal((
    <>
      <div className="admin-review-scrim" aria-hidden="true" onClick={onClose} />
      <aside className="admin-review-drawer" aria-label="Admin conversation review">
        <header className="admin-review-drawer__header">
          <div>
            <span>Conversation review</span>
            <h2>{identity}</h2>
            {user?.displayName && user?.email && <p>{user.email}</p>}
          </div>
          <button
            type="button"
            className="admin-review-close"
            aria-label="Close conversation review"
            title="Close conversation review"
            onClick={onClose}
          >
            <X size={19} aria-hidden="true" />
          </button>
        </header>

        <div className="admin-review-campaign">
          <label htmlFor="admin-review-campaign-select">Campaign</label>
          <select
            id="admin-review-campaign-select"
            value={selectedClientId || ''}
            disabled={!campaigns.length}
            onChange={event => onSelectCampaign(event.target.value)}
          >
            <option value="">Choose a campaign</option>
            {campaigns.map(campaign => (
              <option value={campaign.id} key={campaign.id}>{campaignLabel(campaign)}</option>
            ))}
          </select>
        </div>

        {!loading && !error && (showPartial
          || showMetadataShortening
          || (selectedClientId && (shortenedMessages > 0 || shortenedDrafts > 0))) && (
          <div className="admin-review-notices">
            {showPartial && (
              <div
                className="admin-review-partial"
                role="status"
                aria-label="Partial conversation history"
              >
                <AlertTriangle size={16} aria-hidden="true" />
                <span>Some results reached the review limit. Counts may be partial.</span>
              </div>
            )}
            {selectedClientId && (shortenedMessages > 0 || shortenedDrafts > 0) && (
              <div
                className="admin-review-shortened"
                role="status"
                aria-label="Shortened conversation content"
              >
                <FileText size={15} aria-hidden="true" />
                <span>{shortenedCopy}</span>
              </div>
            )}
            {showMetadataShortening && (
              <div
                className="admin-review-shortened"
                role="status"
                aria-label="Shortened conversation metadata"
              >
                <FileText size={15} aria-hidden="true" />
                <span>Some labels, addresses, or recipient lists were shortened.</span>
              </div>
            )}
          </div>
        )}

        <div className="admin-review-content">{content}</div>
      </aside>
    </>
  ), document.body);
}
